import axios from "axios";
import isArray from "lodash/isArray";
import isEmpty from "lodash/isEmpty";
import isFunction from "lodash/isFunction";
import isNil from "lodash/isNil";
import { v4 as uuidv4 } from "uuid";
import { useAxiosHook } from "./useAxiosHook";
import { usePipeline } from "../pipelines/PipelineContext";
import { getPipeline } from "../pipelines/pipelineConfig";
import { validateRequiredFields } from "../_src_shared/utils/functions/validateRequiredFields";
import { removeEmptyArrays } from "../_src_shared/utils/objects/removeEmptyArrays";
import { isLoanReadStale, loanWriteSeq } from "../utils/loan/loanWriteSeq";

/**
 * @param {{ pipelineKey?: string }} [opts]  `pipelineKey` forces the pipeline
 *        instead of reading it from context. Only for callers that sit ABOVE
 *        the per-route <PipelineProvider> — the realtime provider is the one
 *        today — where context always resolves preclose, so a read for an
 *        acquisition hits /underwriting/loan/{id} and 404s with
 *        "Loan is in the Acquisitions pipeline". Everything inside the
 *        drilldown leaves it unset.
 */
export const useUnderwritingHook = ({ pipelineKey } = {}) => {
  // The route prefix comes from the active pipeline (PipelineContext), which
  // defaults to "underwriting". Under <PipelineProvider pipeline="acquisitions">
  // every call below re-points to /loan_acquisitions/* — the acquisitions router
  // mirrors this surface name-for-name, so the whole drilldown is reusable.
  const contextPipeline = usePipeline();
  const {
    apiPrefix: ROUTE_PREFIX,
    notesPath,
    notesLabel,
    pipelineListPath,
  } = pipelineKey ? getPipeline(pipelineKey) : contextPipeline;

  const {
    axiosPostIsLoading,
    isLoading,
    axiosGetIsLoading,
    axiosError,
    axiosGetRequest,
    axiosPostRequest,
  } = useAxiosHook();

  const loading = isLoading || axiosGetIsLoading || axiosPostIsLoading;
  const error = axiosError;

  const getUnderwritingPipeline = async ({
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    // The list route is NOT named the same in both pipelines —
    // /underwriting/underwriting_pipeline vs
    // /loan_acquisitions/acquisitions_pipeline — so it comes from the config,
    // not from the prefix alone.
    const endpoint = `${ROUTE_PREFIX}/${pipelineListPath}`;
    const source = axios.CancelToken.source();
    const params = {
      name: "Get Underwriting Pipeline",
      path: endpoint,
      cancelSource: source,
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    };
    await axiosGetRequest(params);
  };

  const populateLoanFormFromExcel = async ({
    excel,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    // `instanceof File`, NOT isEmpty. A File carries its data internally and has
    // no own enumerable properties, so lodash's isEmpty reports EVERY File as
    // empty — this guard used to reject its own valid input and return early,
    // meaning the sizer import never issued a request at all.
    if (!(excel instanceof File)) {
      console.error("Excel file is missing or is not a File", { excel });
      return;
    }
    const endpoint = `${ROUTE_PREFIX}/new_loan/populate_loan_form`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Populate Loan Form From Excel",
      path: endpoint,
      // buildUploadFormData expects metadata entries wrapping the File — a bare
      // File has no `.file`, so it was skipped and the upload aborted before the
      // network. `populate_loan_form` then reads uploadMetadata[0] positionally
      // (it does NOT match on id, unlike the /files routes), so one entry is all
      // it needs — but the entry has to exist or the server indexes into None.
      files: [
        {
          id: uuidv4(),
          file: excel,
          name: excel.name,
          file_display_name: excel.name,
        },
      ],
      formData: true,
      // errorTesting: true,
      cancelSource: source,
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    });
  };

  // -------------------------------------
  //  POST /underwriting/new_loan/populate_from_loan_application
  // -------------------------------------
  // The PDF twin of populateLoanFormFromExcel: reads a loan application PDF and
  // RETURNS the populated create-loan form without persisting anything. Same
  // multipart shape (one `uploadFiles` part wrapped in a metadata entry), same
  // populated-loan response. This replaced the old workflows-app import (its
  // Mistral OCR was retired) — one request to the LOS, no progress stream.
  //
  // Reading the PDF plus the AI extraction can run up to a couple of minutes;
  // the axios clients carry no timeout, so don't add one here. Errors arrive as
  // `detail`: 400 (no file / empty / not a PDF), 422 (no readable text, e.g. a
  // blank scan), 500 (anything else).
  const populateLoanFormFromLoanApplication = async ({
    pdf,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    if (!(pdf instanceof File)) {
      console.error("Loan application PDF is missing or is not a File", { pdf });
      return;
    }
    const endpoint = `${ROUTE_PREFIX}/new_loan/populate_from_loan_application`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Populate Loan Form From Loan Application",
      path: endpoint,
      files: [
        {
          id: uuidv4(),
          file: pdf,
          name: pdf.name,
          file_display_name: pdf.name,
        },
      ],
      formData: true,
      cancelSource: source,
      // The card renders the server's `detail` inline; a snackbar on top of it
      // would say the same thing twice.
      hideFailMsg: true,
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    });
  };

  // -------------------------------------
  //  POST /underwriting/new_loan/import_from_excel
  // -------------------------------------
  // Sizer Import → Create Loan. Synchronously parses the uploaded sizer .xlsx
  // (product auto-detected: DSCR / GUC / MF / RTL, falling back to the
  // standardized named-range intake template) and CREATES the loan in the
  // underwriting pipeline, returning the full loan object — same shape as the
  // loan-detail view. Navigate to the created loan by `_id` in onSuccessFn.
  //
  // The endpoint reads the uploading user's email from the auth token, so the
  // only multipart fields are `uploadFile` (singular) and the optional
  // `create_salesforce_opportunity` flag. We build the FormData by hand and pass
  // it straight through as the payload (NOT formData:true, which appends under
  // `uploadFiles`/`submission` to match the other multipart routes).
  const createLoanFromExcel = async ({
    excel,
    createSalesforceOpportunity = false,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    // FileUploadField hands back { file: File, ... } wrappers; tolerate a raw
    // File too.
    const file = excel?.file instanceof File ? excel.file : excel;
    if (isNil(file) || !(file instanceof File)) {
      console.error("Sizer Excel file is missing", { excel });
      return;
    }
    const endpoint = `${ROUTE_PREFIX}/new_loan/import_from_excel`;
    const source = axios.CancelToken.source();

    const fd = new FormData();
    fd.append("uploadFile", file, file.name);
    fd.append(
      "create_salesforce_opportunity",
      createSalesforceOpportunity ? "true" : "false",
    );

    await axiosPostRequest({
      name: "Create Loan From Sizer Excel",
      path: endpoint,
      payload: fd,
      headers: { "Content-Type": "multipart/form-data" },
      cancelSource: source,
      onSuccessMsg: "Loan created from sizer",
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    });
  };

  const createLoan = async ({ data, onSuccessFn, onCompleteFn, onFailFn }) => {
    if (isNil(data) || isEmpty(data)) {
      console.error("Loan data is missing", { data });
      return;
    }
    const endpoint = `${ROUTE_PREFIX}/create_loan`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Create Loan",
      path: endpoint,
      payload: data,
      cancelSource: source,
      onSuccessFn,
      // errorTesting: true,
      onSuccessMsg: "Loan created successfully",
      onCompleteFn,
      onFailFn,
    });
  };

  const createLoanWithFileUploads = async ({
    data,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    if (isNil(data) || isEmpty(data)) {
      console.error("Form data is missing", { data });
      return;
    }
    const endpoint = `${ROUTE_PREFIX}/create_loan_file_uploads`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Create Loan With File Uploads",
      path: endpoint,
      formData: true,
      payload: data,
      cancelSource: source,
      onSuccessFn,
      // errorTesting: true,
      onCompleteFn,
      onFailFn,
    });
  };

  const createLoanFromApplication = async ({
    applicationId,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    if (isNil(applicationId)) {
      console.error("applicationId is missing", { applicationId });
      return;
    }
    const endpoint = `${ROUTE_PREFIX}/create_deal_from_submission/loanApplication/${applicationId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Create Loan From Application",
      path: endpoint,
      // formData: true,

      // errorTesting: true,
      cancelSource: source,
      onSuccessFn,

      onCompleteFn,
      onFailFn,
    });
  };

  // Multi use
  const createLoanFromSubmission = async ({
    applicationId,
    submissionType,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    validateRequiredFields({
      applicationId,
      submissionType, // 'loanApplication', 'quickApp', 'creditAuthorization'
    });
    const endpoint = `${ROUTE_PREFIX}/create_deal_from_submission/${submissionType}/${applicationId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: `Create Loan From Submission: ${submissionType}`,
      path: endpoint,
      cancelSource: source,
      onSuccessFn,

      onCompleteFn,
      onFailFn,
    });
  };

  const getLoanById = async ({
    loanId,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
    // Escape hatch for a read that must be applied even if a write raced it
    // (e.g. an explicit "Refresh loan" the user asked for).
    allowStale = false,
  }) => {
    const endpoint = `${ROUTE_PREFIX}/loan/${loanId}`;
    const source = axios.CancelToken.source();
    // Snapshot the loan's write counter BEFORE the request goes out. If a
    // mutation lands while we're in flight, its response already wrote a
    // newer loan into Redux and ours would revert it — see
    // utils/loan/loanWriteSeq.js.
    const readToken = loanWriteSeq(loanId);
    const params = {
      name: "Get Loan By ID",
      path: endpoint,
      cancelSource: source,
      onSuccessFn: (data) => {
        if (!allowStale && isLoanReadStale(loanId, readToken)) return;
        if (isFunction(onSuccessFn)) onSuccessFn(data);
      },
      onCompleteFn,
      onFailFn,
    };
    await axiosGetRequest(params);
  };

  // -------------------------------------
  //  GET /underwriting/loan_events/{dealId}
  //  Structured audit-trail events for the loan activity log.
  //  Returns { events: [...newest-first], total_events }.
  // -------------------------------------
  const getLoanEvents = async ({
    dealId,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    if (isNil(dealId)) {
      console.error("getLoanEvents: missing dealId");
      return;
    }
    const endpoint = `${ROUTE_PREFIX}/loan_events/${dealId}`;
    const source = axios.CancelToken.source();
    const params = {
      name: "Get Loan Events",
      path: endpoint,
      cancelSource: source,
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    };
    await axiosGetRequest(params);
  };

  const updateLoanData = async ({
    loanId,
    data,
    onSuccessFn,
    onSuccessMsg = "Loan data updated successfully",
    onCompleteFn,
    hideFailMsg = false,
    onFailFn,
  }) => {
    if (isNil(loanId) || isNil(data) || isEmpty(data)) {
      console.error("Loan ID or Data is missing", { loanId, data });
      return;
    }
    const endpoint = `${ROUTE_PREFIX}/update_loan_data/${loanId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Update Loan Data",
      path: endpoint,
      payload: data,
      cancelSource: source,
      onSuccessFn,
      onSuccessMsg: onSuccessMsg,
      // errorTesting: true,
      onCompleteFn,
      hideFailMsg,
      onFailFn,
    });
  };

  const unsetLoanDataFields = async ({
    loanId,
    data,
    onSuccessFn,
    onSuccessMsg = "Loan data updated successfully",
    onCompleteFn,
    hideFailMsg = false,
    onFailFn,
  }) => {
    if (isNil(loanId) || isNil(data) || isEmpty(data)) {
      console.error("Loan ID or Data is missing", { loanId, data });
      return;
    }
    const endpoint = `${ROUTE_PREFIX}/unset_fields/${loanId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Unset Loan Data Fields",
      path: endpoint,
      payload: data,
      cancelSource: source,
      onSuccessFn,
      onSuccessMsg: onSuccessMsg,
      // errorTesting: true,
      onCompleteFn,
      hideFailMsg,
      onFailFn,
    });
  };

  const updateUnderwritingNotes = async ({
    loanId,
    data,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    if (isNil(loanId) || isNil(data) || isEmpty(data)) {
      console.error("Loan ID or Data is missing", { loanId, data });
      return;
    }
    // Acquisitions renames this route segment to `diligence_notes` (see
    // pipelineConfig.notesPath) — one of the few paths the two routers DON'T
    // share, so it can't ride on ROUTE_PREFIX alone.
    const endpoint = `${ROUTE_PREFIX}/${notesPath}/${loanId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: `Update ${notesLabel}`,
      path: endpoint,
      payload: data,
      cancelSource: source,
      onSuccessFn,
      // errorTesting: true,
      onSuccessMsg: `${notesLabel} updated successfully`,
      onCompleteFn,
      onFailFn,
    });
  };

  const updateMilestoneStatus = async ({
    loanId,
    loanStatus,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    validateRequiredFields({ loanId, loanStatus });
    const endpoint = `${ROUTE_PREFIX}/update_milestone/${loanId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Update Milestone Status",
      path: endpoint,
      payload: { loanStatus },
      cancelSource: source,
      onSuccessFn,
      onSuccessMsg: `Milestone status updated successfully to ${loanStatus}`,
      // errorTesting: true,
      onCompleteFn,
      onFailFn,
    });
  };

  const updateLoanStatusOrSubStatus = async ({
    loanId,
    loanStatus,
    subStatus,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    validateRequiredFields({
      loanId,
      loanStatus,
    });
    const endpoint = `${ROUTE_PREFIX}/update_milestone/${loanId}`;
    const source = axios.CancelToken.source();
    const payload = { loanStatus, subStatus };

    // console.log("Payload for updating loan status or sub status", payload);
    // return;

    await axiosPostRequest({
      name: "Update Loan Status",
      path: endpoint,
      payload: payload,
      cancelSource: source,
      onSuccessFn,
      // errorTesting: true,
      onSuccessMsg: `Loan status updated successfully to ${loanStatus}`,
      onCompleteFn,
      onFailFn,
    });
  };

  // POST /underwriting/update_sub_status/{dealId} { subStatus }
  // (loan_and_auth_endpoints.md §6) — updates ONLY the sub-status without
  // touching the milestone. Use this when the loanStatus shouldn't change
  // (e.g. "Appraisal Ordered" → "Appraisal In"); use `updateMilestone` /
  // `updateLoanStatusOrSubStatus` when advancing the milestone itself.
  const updateSubStatus = async ({
    dealId,
    subStatus,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    validateRequiredFields({ dealId, subStatus });
    const endpoint = `${ROUTE_PREFIX}/update_sub_status/${dealId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Update Sub Status",
      path: endpoint,
      payload: { subStatus },
      cancelSource: source,
      onSuccessFn,
      onSuccessMsg: `Sub-status updated successfully to ${subStatus}`,
      onCompleteFn,
      onFailFn,
    });
  };

  const createDealComment = async ({
    loanId,
    data,
    onSuccessFn,
    onCompleteFn,
    testing = false,
    onFailFn,
    // Defaults to the standard success snackbar; pass null/"" to suppress it
    // (e.g. when fanning out an exception comment so we don't double-toast).
    onSuccessMsg = "Comment created successfully",
  }) => {
    if (isNil(loanId) || isNil(data) || isEmpty(data)) {
      console.error("Loan ID or Data is missing", { loanId, data });
      return;
    }
    const endpoint = `${ROUTE_PREFIX}/create_comment/${loanId}`;
    const source = axios.CancelToken.source();

    // console.log("data", data);
    // return;
    await axiosPostRequest({
      name: "Create Deal Comment",
      path: endpoint,
      payload: data,
      cancelSource: source,
      onSuccessFn,
      onSuccessMsg,
      // errorTesting: testing,
      onCompleteFn,
      onFailFn,
    });
  };

  const createEnhancedDealComment = async ({
    loanId,
    data,
    onSuccessFn,
    onCompleteFn,
    testing = false,
    onFailFn,
  }) => {
    if (isNil(loanId) || isNil(data) || isEmpty(data)) {
      console.error("Loan ID or Data is missing", { loanId, data });
      return;
    }
    // const endpoint = `${ROUTE_PREFIX}/create_enhanced_deal_comment/${loanId}`;
    const endpoint = `${ROUTE_PREFIX}/create_comment_with_uploads/${loanId}`;
    const source = axios.CancelToken.source();
    // console.log("data", data);
    // return;
    await axiosPostRequest({
      name: "Create Deal Comment",
      path: endpoint,
      payload: data,
      formData:
        isArray(data?.uploadMetadata) && !isEmpty(data?.uploadMetadata)
          ? true
          : false,
      cancelSource: source,
      onSuccessFn,
      onSuccessMsg: "Comment created successfully",
      // errorTesting: true,
      onCompleteFn,
      onFailFn,
    });
  };

  // POST /underwriting/update_comment/{dealId}/{commentId} — edit an existing
  // comment. `data` is a partial of comment fields to $set (e.g. { htmlString }).
  // Returns the updated full loan.
  const updateDealComment = async ({
    loanId,
    commentId,
    data,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
    onSuccessMsg = "Comment updated",
  }) => {
    if (isNil(loanId) || isNil(commentId) || isNil(data) || isEmpty(data)) {
      console.error("Loan ID, comment ID, or data missing", { loanId, commentId, data });
      return;
    }
    const endpoint = `${ROUTE_PREFIX}/update_comment/${loanId}/${commentId}`;
    const source = axios.CancelToken.source();
    await axiosPostRequest({
      name: "Update Deal Comment",
      path: endpoint,
      payload: data,
      cancelSource: source,
      onSuccessFn,
      onSuccessMsg,
      onCompleteFn,
      onFailFn,
    });
  };

  // POST /underwriting/add_loan_counterparty/{dealId} { investorCounterparty }
  // Sets the loan's investor/counterparty. Returns the updated full loan.
  const addLoanCounterparty = async ({
    loanId,
    investorCounterparty,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
    onSuccessMsg = "Counterparty updated",
  }) => {
    if (isNil(loanId)) {
      console.error("Loan ID is missing", { loanId });
      return;
    }
    const endpoint = `${ROUTE_PREFIX}/add_loan_counterparty/${loanId}`;
    const source = axios.CancelToken.source();
    await axiosPostRequest({
      name: "Add Loan Counterparty",
      path: endpoint,
      payload: { investorCounterparty },
      cancelSource: source,
      onSuccessFn,
      onSuccessMsg,
      onCompleteFn,
      onFailFn,
    });
  };

  // POST /underwriting/find_contact_by_email { emailAddress } → an existing
  // contact doc (or null). Used to pre-fill the add-contact flow.
  const findContactByEmail = async ({
    emailAddress,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    if (isNil(emailAddress)) {
      console.error("emailAddress is missing");
      return;
    }
    const endpoint = `${ROUTE_PREFIX}/find_contact_by_email`;
    const source = axios.CancelToken.source();
    await axiosPostRequest({
      name: "Find Contact By Email",
      path: endpoint,
      payload: { emailAddress },
      cancelSource: source,
      onSuccessFn,
      onSuccessMsg: "",
      onCompleteFn,
      onFailFn,
    });
  };

  const addPermittedUserToLoan = async ({
    loanId,
    emailAddress,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    if (isNil(loanId) || isNil(emailAddress)) {
      console.error("Loan ID or Data is missing", { loanId, emailAddress });
      return;
    }
    const endpoint = `${ROUTE_PREFIX}/add_permitted_users/${loanId}`;
    const source = axios.CancelToken.source();
    const params = {
      name: "Add Permitted User To Loan",
      path: endpoint,
      payload: {
        emailAddress: emailAddress,
      },
      cancelSource: source,
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    };
    await axiosPostRequest(params);
  };

  const updateLoanPermittedUsers = async ({
    loanId,
    permittedUsers,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    if (isNil(loanId) || isNil(permittedUsers) || !isArray(permittedUsers)) {
      console.error("Loan ID or Data is missing", { loanId, permittedUsers });
      return;
    }
    const endpoint = `${ROUTE_PREFIX}/add_permitted_users/${loanId}`;
    const source = axios.CancelToken.source();
    const params = {
      name: "Add Permitted User To Loan",
      path: endpoint,
      payload: {
        permittedUsers,
      },
      cancelSource: source,
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    };
    await axiosPostRequest(params);
  };

  const createUnnestedObject = async ({
    objectName, //  titleReport', 'underwritingCheckList', 'loanLock', 'borrowerEntity'
    loanId,
    data,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    if (isNil(loanId) || isNil(objectName) || isNil(data)) {
      console.error("Loan ID, Object Name, or Data is missing", {
        loanId,
        objectName,
        data,
      });
      return;
    }

    const objectNameOptions = [
      "titleReport",
      "underwritingCheckList",
      "loanLock",
      "borrowerEntity",
    ];
    if (!objectNameOptions.includes(objectName)) {
      console.error("Invalid object name", { objectName });
      return;
    }
    const endpoint = `${ROUTE_PREFIX}/create_object/${objectName}/${loanId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Create Unnested Object",
      path: endpoint,
      payload: data,
      cancelSource: source,
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    });
  };

  const updateUnnestedObject = async ({
    objectName, //  titleReport', 'underwritingCheckList', 'loanLock', 'borrowerEntity'
    loanId,
    data,
    onSuccessFn,
    onCompleteFn,
    onSuccessMsg = "Updated successfully",
    onFailFn,
  }) => {
    if (isNil(loanId) || isNil(objectName) || isNil(data)) {
      console.error("Loan ID, Object Name, or Data is missing", {
        loanId,
        objectName,
        data,
      });
      return;
    }

    const objectNameOptions = [
      "titleReport",
      "underwritingCheckList",
      "loanLock",
      "borrowerEntity",
    ];
    if (!objectNameOptions.includes(objectName)) {
      console.error("Invalid object name", { objectName });
      return;
    }
    const endpoint = `${ROUTE_PREFIX}/update_object/${objectName}/${loanId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Update Unnested Object",
      path: endpoint,
      payload: data,
      cancelSource: source,
      onSuccessFn,
      onSuccessMsg,
      onCompleteFn,
      onFailFn,
    });
  };

  const createLoanException = async ({
    loanId,
    data,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    if (isNil(loanId) || isNil(data) || isEmpty(data)) {
      console.error("Loan ID or Data is missing", { loanId, data });
      return;
    }
    const endpoint = `${ROUTE_PREFIX}/create_loan_exception/${loanId}`;
    const source = axios.CancelToken.source();
    const params = {
      name: "Create Loan Exception",
      path: endpoint,
      payload: data,
      cancelSource: source,
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    };
    await axiosPostRequest(params);
  };

  const createObjectInList = async ({
    objectName,
    loanId,
    data,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    const endpoint = `${ROUTE_PREFIX}/create_list_object/${objectName}/${loanId}`;
    const source = axios.CancelToken.source();
    const params = {
      name: "Create Object In List",
      path: endpoint,
      payload: data,
      cancelSource: source,
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    };
    await axiosPostRequest(params);
  };

  const updateObjectInList = async ({
    objectName,
    loanId,
    _id,
    data,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    const endpoint = `${ROUTE_PREFIX}/update_list_object/${objectName}/${loanId}/${_id}`;
    const source = axios.CancelToken.source();
    const params = {
      name: "Update Object In List",
      path: endpoint,
      payload: data,
      cancelSource: source,
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    };
    await axiosPostRequest(params);
  };

  const createSubjectProperty = async ({
    loanId,
    propertyData,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    if (isNil(loanId) || isNil(propertyData)) {
      console.error("Loan ID or propertyData is missing", {
        loanId,
        propertyData,
      });
      return;
    }
    const endpoint = `${ROUTE_PREFIX}/create_subject_property/${loanId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Create Subject Property",
      path: endpoint,
      payload: propertyData,
      cancelSource: source,
      onSuccessMsg: "Property created successfully",
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    });
  };

  const updateSubjectProperty = async ({
    loanId,
    propertyId,
    data,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    const endpoint = `${ROUTE_PREFIX}/update_subject_property/${loanId}/${propertyId}`;
    const source = axios.CancelToken.source();
    if (isNil(loanId) || isNil(propertyId) || isNil(data)) {
      console.error("Loan ID or Property ID is missing", {
        loanId,
        propertyId,
        data,
      });
      return;
    }

    await axiosPostRequest({
      name: "Update Subject Property",
      path: endpoint,
      payload: data,
      cancelSource: source,
      onSuccessMsg: "Property updated successfully",
      // errorTesting: true,
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    });
  };

  const createSubjectPropertyUnit = async ({
    loanId,
    propertyId,
    propertyData,
    onSuccessMsg = "Property created successfully",
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    if (isNil(loanId) || isNil(propertyData) || isNil(propertyId)) {
      console.error("Loan ID or propertyData is missing", {
        loanId,
        propertyData,
        propertyId,
      });
      return;
    }
    const endpoint = `${ROUTE_PREFIX}/create_unit/${loanId}/${propertyId}`;
    const source = axios.CancelToken.source();
    // console.log("Submitting", { loanId, propertyId, propertyData });
    // return;
    await axiosPostRequest({
      name: "Create Subject Property Unit",
      path: endpoint,
      payload: propertyData,
      cancelSource: source,
      onSuccessMsg,
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    });
  };

  const updateSubjectPropertyUnit = async ({
    loanId,
    propertyId,
    unitId,
    unitData,
    onSuccessMsg = "Property updated successfully",
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    const endpoint = `${ROUTE_PREFIX}/update_unit/${loanId}/${propertyId}/${unitId}`;
    const source = axios.CancelToken.source();
    if (
      isNil(loanId) ||
      isNil(propertyId) ||
      isNil(unitData) ||
      isNil(unitId)
    ) {
      console.error("Loan ID or Property ID is missing", {
        loanId,
        propertyId,
        unitData,
      });
      return;
    }
    // Don't fire an empty PATCH-style update: FastAPI's body reader trips
    // on a zero-length body when the endpoint expects a Pydantic model
    // (anyio WouldBlock → MemoryObjectItemReceiver AttributeError) and
    // the backend returns a 500. Bail early instead.
    if (isEmpty(unitData)) {
      console.warn("Skipping update_unit — no changed fields provided");
      onCompleteFn?.();
      return;
    }

    // console.log("--------", { loanId, propertyId, unitId, unitData });
    // return;
    await axiosPostRequest({
      name: "Update Subject Property Unit",
      path: endpoint,
      payload: unitData,
      cancelSource: source,
      onSuccessMsg,
      // errorTesting: true,
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    });
  };

  const createBorrower = async ({
    loanId,
    borrower,

    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    const endpoint = `${ROUTE_PREFIX}/create_borrower/${loanId}`;
    const source = axios.CancelToken.source();

    if (isNil(loanId) || isNil(borrower)) {
      console.error("Loan ID or Borrower data is missing", {
        loanId,
        borrower,
      });
      return;
    }
    await axiosPostRequest({
      name: "Create Borrower",
      path: endpoint,
      payload: borrower,
      cancelSource: source,
      onSuccessMsg: "Borrower created successfully",

      onSuccessFn,
      onCompleteFn,
      onFailFn,
    });
  };

  const updateBorrower = async ({
    loanId,
    borrowerId,
    data,

    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    if (isNil(loanId) || isNil(borrowerId) || isNil(data)) {
      console.error("Loan ID or Borrower ID is missing", {
        loanId,
        borrowerId,
        data,
      });
      return;
    }
    const endpoint = `${ROUTE_PREFIX}/update_borrower/${loanId}/${borrowerId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Update Borrower",
      path: endpoint,
      payload: data,
      cancelSource: source,
      onSuccessMsg: "Borrower updated successfully",

      onSuccessFn,
      onCompleteFn,
      onFailFn,
    });
  };

  const assignSubmissionToDeal = async ({
    borrower_submission,
    loanNumber,
    data,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    const endpoint = `${ROUTE_PREFIX}/assign_to_deal/${borrower_submission}/${loanNumber}`;
    const source = axios.CancelToken.source();
    const params = {
      name: "Assign Submission To Deal",
      path: endpoint,
      payload: data,
      cancelSource: source,
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    };
    await axiosPostRequest(params);
  };

  const addLoanTag = async ({ dealId, tags, onSuccessFn, onFailFn }) => {
    if (isNil(dealId) || isNil(tags)) {
      console.error("Deal ID or Tag is missing", { dealId, tags });
      return;
    }

    const endpoint = `${ROUTE_PREFIX}/add_loan_tag/${dealId}`;
    const source = axios.CancelToken.source();
    const params = {
      name: "Add Loan Tag",
      path: endpoint,
      payload: { tags: tags },
      cancelSource: source,
      onSuccessFn,
      onFailFn,
    };
    await axiosPostRequest(params);
  };

  const removeLoanTag = async ({ dealId, tag, onSuccessFn, onFailFn }) => {
    if (isNil(dealId) || isNil(tag)) {
      console.error("Deal ID or Tag is missing", { dealId, tag });
      return;
    }

    // console.log("Tag", tag);
    // return;
    const endpoint = `${ROUTE_PREFIX}/remove_loan_tag/${dealId}`;
    const source = axios.CancelToken.source();
    const params = {
      name: "Remove Loan Tag",
      path: endpoint,
      payload: { tags: [tag] },
      // payload: data,
      cancelSource: source,
      onSuccessFn,
      onFailFn,
    };
    await axiosPostRequest(params);
  };

  const addLoanRelatedContact = async ({
    dealId,
    contactData,
    onSuccessFn,
    onFailFn,
  }) => {
    if (isNil(dealId) || isNil(contactData)) {
      console.error("Deal ID or Contact Data is missing", {
        dealId,
        contactData,
      });
      return;
    }

    const endpoint = `${ROUTE_PREFIX}/add_loan_related_contact/${dealId}`;
    const source = axios.CancelToken.source();
    const params = {
      name: "Add Loan Related Contact",
      path: endpoint,
      payload: contactData,
      cancelSource: source,
      onSuccessFn,
      onFailFn,
    };
    await axiosPostRequest(params);
  };

  const updateLoanRelatedContact = async ({
    dealId,
    contactId,
    contactData,
    onSuccessFn,
    onFailFn,
  }) => {
    if (isNil(dealId) || isNil(contactId) || isNil(contactData)) {
      console.error("Deal ID or Contact Data is missing", {
        dealId,
        contactId,
        contactData,
      });
      return;
    }

    const endpoint = `${ROUTE_PREFIX}/update_loan_related_contact/${dealId}/${contactId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Update Loan Related Contact",
      path: endpoint,
      payload: contactData,
      cancelSource: source,
      onSuccessFn,
      onFailFn,
    });
  };

  // POST /{prefix}/delete_loan_related_contact/{dealId}/{contactId}
  // Pulls the contact off the loan and pushes a copy into
  // `deletedLoanRelatedContacts[]` (the deletedBorrowers[] convention), then
  // unlinks it from the contacts collection. Returns the updated loan.
  // 404 = the loan isn't in this pipeline, or the row is stale (refresh);
  // 409 = a bulk-backfilled contact with no `_id`, which can't be deleted.
  const deleteLoanRelatedContact = async ({
    dealId,
    contactId,
    onSuccessFn,
    onFailFn,
  }) => {
    if (isNil(dealId) || isNil(contactId)) {
      console.error("Deal ID or Contact ID is missing", { dealId, contactId });
      return;
    }

    const endpoint = `${ROUTE_PREFIX}/delete_loan_related_contact/${dealId}/${contactId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Delete Loan Related Contact",
      path: endpoint,
      payload: {},
      cancelSource: source,
      onSuccessFn,
      onFailFn,
    });
  };

  const addAssociation = async ({
    dealId,
    associationData,
    associationTypeName, // 'borrowers', 'subjectProperties', 'documents'
    associationTypeId,
    onSuccessFn,
    onFailFn,
  }) => {
    /**
      loanDocuments : List[PyObjectId] | None = Field(default = [], description = 'list of documents associated with the comment')
      borrowers : List[PyObjectId] | None = Field(default = None, description = 'list of borrower ids associated with the comment')
      subjectProperties : List[PyObjectId] | None = Field(default = None, description = 'list of subject property ids associated with the comment')
      applications : List[PyObjectId] | None = Field(default = None, description = 'list of application ids associated with the comment')
      creditAuthorizations : List[PyObjectId] | None = Field(default = None, description = 'list of credit authorization ids associated with the comment')
      conditions : List[PyObjectId] | None = Field(default = None, description = 'list of underwriting condition ids associated with the comment')
      taskListItems : List[PyObjectId] | None = Field(default = None, description = 'list of task list item ids associated with the comment')
      managementExceptions : List[PyObjectId] | None = Field(default = None, description = 'list of management exception ids associated with the comment')
      comments : List[PyObjectId] | None = Field(default = None, description = 'list of comment ids associated with the comment')
      tasks : List[PyObjectId] | None = Field(default = None, description = 'list of task ids associated with the comment')
      */
    // console.log("Creating association", {
    //   dealId,
    //   associationData,
    //   associationTypeName,
    //   associationTypeId,
    // });
    validateRequiredFields({
      dealId,
      associationTypeName, // 'borrowers', 'subjectProperties', 'documents'
      associationTypeId,
      associationData,
    });
    const endpoint = `${ROUTE_PREFIX}/create_association/${dealId}/${associationTypeName}/${associationTypeId}`;
    const source = axios.CancelToken.source();

    const cleanedPayload = removeEmptyArrays(associationData);
    // console.log("--- ADD REQUEST --- ", {
    //   name: `Add Association to ${associationTypeName}`,
    //   path: endpoint,
    //   payload: cleanedPayload,
    //   cancelSource: source,
    //   onSuccessFn,
    //   onFailFn,
    // });
    // if (onSuccessFn) {
    //   onSuccessFn();
    // }
    // return;
    await axiosPostRequest({
      name: `Add Association to ${associationTypeName}`,
      path: endpoint,
      payload: cleanedPayload,

      // payload: {
      //   borrowers: [],
      //   subjectProperties: [],
      //   loanDocuments: ["68da9bef9c0656e40667b68c"],
      // },
      // payload: {
      //   association: {
      //     borrowers: [],
      //     subjectProperties: [],
      //     loanDocuments: ["68da9bef9c0656e40667b68c"],
      //   },
      // },
      // payload: associationData,
      // payload: {
      //   associations: associationData,
      // },
      cancelSource: source,
      onSuccessFn,
      onFailFn,
    });
  };

  const removeAssociation = async ({
    dealId,
    associationTypeName, // 'borrowers', 'subjectProperties', 'documents'
    associationTypeId, // borrwer?.id
    associationIdToDelete,
    associationToDeleteName = "loanDocuments", // 'borrowers', 'subjectProperties', 'loanDocuments'
    onSuccessFn,
    onFailFn,
  }) => {
    validateRequiredFields({
      dealId,
      associationIdToDelete,
      associationTypeName, // 'borrowers', 'subjectProperties', 'documents'
      associationTypeId,
      associationToDeleteName,
    });

    /**
     * @router.post("/delete_association/{dealId}/{referenceObjectName}/{referenceObjectId}/{associatedObjectName}/{remove_association_id}")
     * @router.post("/delete_association/{dealId}/{'borrowers'}/{borrowers[0]?._id}/{'loanDocuments'}/{remove_association_id}")
     */
    const endpoint = `${ROUTE_PREFIX}/delete_association/${dealId}/${associationTypeName}/${associationTypeId}/${associationToDeleteName}/${associationIdToDelete}`;
    const source = axios.CancelToken.source();
    // console.log("Removing association", {
    //   dealId,
    //   associationTypeName, // 'borrowers', 'subjectProperties', 'documents'
    //   associationTypeId, // borrwer?.id
    //   associationIdToDelete,
    // });

    // console.log("--- REMOVE REQUEST --- ", {
    //   name: `Remove Associations to ${associationTypeName}`,
    //   path: endpoint,
    //   payload: {},
    //   cancelSource: source,
    //   onSuccessFn,
    //   onFailFn,
    // });
    // if (onSuccessFn) {
    //   onSuccessFn();
    // }
    // return;
    await axiosPostRequest({
      name: `Remove Associations to ${associationTypeName}`,
      path: endpoint,
      payload: {},
      cancelSource: source,
      onSuccessFn,
      onFailFn,
    });
  };

  // `payload` is `{ closingDate }` — the only key mark_deal_closed reads. It
  // sets loanStatus=Closed and derives originationDate / estimatedClosingDate
  // from it, falling back to "now" when omitted.
  const markDealClosed = async ({ dealId, payload, onSuccessFn, onFailFn }) => {
    validateRequiredFields({
      dealId,
    });

    const endpoint = `${ROUTE_PREFIX}/mark_deal_closed/${dealId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: `Deal Marked Closed Successfully`,
      path: endpoint,
      payload: payload || {},
      cancelSource: source,
      onSuccessFn,
      onFailFn,
    });
  };

  const markDealDead = async ({ dealId, payload, onSuccessFn, onFailFn }) => {
    validateRequiredFields({
      dealId,
      payload,
    });

    const endpoint = `${ROUTE_PREFIX}/mark_deal_dead/${dealId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: `Deal Marked Dead Successfully`,
      path: endpoint,
      payload: payload,
      cancelSource: source,
      onSuccessFn,
      onFailFn,
    });
  };

  // Borrower classification override — one route for BOTH pipelines, so it is
  // not under ROUTE_PREFIX. Body: { borrower_classification_override,
  // borrower_classification_override_note }; a class needs a note, clearing
  // (null) does not. Returns the override fields + borrowerClassificationDetails
  // + exceptionCheck, not the loan. 403 / 409 / 422 are shown by the modal.
  const setBorrowerClassificationOverride = async ({
    dealId,
    payload,
    onSuccessFn,
    onFailFn,
  }) => {
    validateRequiredFields({
      dealId,
      payload,
    });

    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: payload?.borrower_classification_override
        ? `Classification Override Saved`
        : `Classification Override Cleared`,
      path: `borrower_classification/override/${dealId}`,
      payload,
      cancelSource: source,
      suppressFailStatuses: [403, 409, 422],
      onSuccessFn,
      onFailFn,
    });
  };

  const getZillowZipcodeData = async ({ dealId, onSuccessFn, onFailFn }) => {
    validateRequiredFields({
      dealId,
    });

    const endpoint = `${ROUTE_PREFIX}/get_zillow_zipcode_data/${dealId}`;
    const source = axios.CancelToken.source();

    await axiosGetRequest({
      // name: `Zillow Zipcode Data Retrieved Successfully`,
      path: endpoint,
      cancelSource: source,
      onSuccessFn,
      onFailFn,
    });
  };

  // -----------
  // -----------

  const searchBorrowerPortalUsers = async ({
    data,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    const endpoint = `${ROUTE_PREFIX}/search_borrower_portal_users`;
    const source = axios.CancelToken.source();

    /**
     	"""Search borrower portal users by firstName, lastName, emailAddress, or borrowerSSN.
	Request body: { firstName?, lastName?, emailAddress?, borrowerSSN?, limit? }
	Returns matching live and staged portal users with key profile fields."""
     */
    await axiosPostRequest({
      name: "Search Borrower Portal Users",
      path: endpoint,
      payload: data,
      cancelSource: source,
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    });
  };

  const searchSalesforceContacts = async ({
    data,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    const endpoint = `${ROUTE_PREFIX}/search_salesforce_contacts`;
    const source = axios.CancelToken.source();
    /**
     * """Search Salesforce contacts by email to pull in existing borrower detail.
	Request body: { emailAddress: string }
	Returns Salesforce contact records with related opportunity/loan history."""
     */
    await axiosPostRequest({
      name: "Search Salesforce Contacts",
      path: endpoint,
      payload: data,
      cancelSource: source,
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    });
  };

  // POST /underwriting/search_loan_contacts
  // { q?, emailAddress?, phone?, licenseNumber?, role?, limit? } — at least one of
  // q (2+ letters/digits), emailAddress, a 10-digit phone or licenseNumber.
  // LIVE search of loan contacts (everyone on a loan who is not the borrower —
  // title agents, appraisers, counsel, brokers and the firms behind them) across
  // EV + LQ loans and Salesforce. Returns { results, total, truncated, currentLos,
  // searched, notes }; Salesforce being down is a 200 with notes, not an error.
  // 400 = nothing searchable (body.error is the message to show under the box).
  // The caller owns the snackbar: cancelled requests must stay silent, and the
  // 400 message belongs under the search box, not in a toast.
  const searchLoanContacts = async ({
    data,
    cancelToken,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    const endpoint = `${ROUTE_PREFIX}/search_loan_contacts`;
    await axiosPostRequest({
      name: "Search Loan Contacts",
      path: endpoint,
      payload: data,
      cancelToken,
      hideFailMsg: true,
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    });
  };

  // POST /underwriting/search_salesforce_entities { entityName } (min 3 chars)
  // → Salesforce borrowing-entity (Account) records keyed by Account Id, each with
  // entityName / entityEINNumber / integrationId / salesforceAccountId and prior
  // EV/LQ loan history (sf_opportunities). Members are not included.
  const searchSalesforceEntities = async ({
    data,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    const endpoint = `${ROUTE_PREFIX}/search_salesforce_entities`;
    const source = axios.CancelToken.source();
    await axiosPostRequest({
      name: "Search Salesforce Entities",
      path: endpoint,
      payload: data,
      cancelSource: source,
      onSuccessFn,
      onSuccessMsg: "",
      onCompleteFn,
      onFailFn,
    });
  };

  const createSalesforceOpportunity = async ({
    dealId,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    validateRequiredFields({ dealId });
    /**
     * POST /underwriting/create_salesforce_opportunity/{dealId}
     * Builds (and, once the backend SF write is enabled, creates) a Salesforce
     * opportunity from the loan. Runs inline and returns the opportunity object.
     */
    const endpoint = `${ROUTE_PREFIX}/create_salesforce_opportunity/${dealId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Create Salesforce Opportunity",
      path: endpoint,
      cancelSource: source,
      onSuccessFn,
      onSuccessMsg: "Salesforce opportunity created",
      onCompleteFn,
      onFailFn,
    });
  };

  const linkBorrowerToPortalUser = async ({
    dealId,
    borrowerId,
    data,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    // On borrower drilldown
    const endpoint = `${ROUTE_PREFIX}/link_borrower_to_portal_user/${dealId}/${borrowerId}`;
    const source = axios.CancelToken.source();
    /**
     	"""Link an existing deal borrower to a borrower portal user by portal user _id or email.
	Copies select profile fields from the portal user onto the deal borrower and stores the portal user _id
	on the borrower for ongoing reference. Also adds the dealId to the portal user's dealIds.
	Request body: { portalUserId?: string, emailAddress?: string }"""
     */

    console.log("Linking borrower to portal user with data", {
      dealId,
      borrowerId,
      data,
    });
    // return;
    await axiosPostRequest({
      name: "Link Borrower to Portal User",
      path: endpoint,
      payload: data,
      cancelSource: source,
      onSuccessFn,
      onSuccessMsg: "Borrower linked to portal user",
      onCompleteFn,
      onFailFn,
    });
  };

  //TODO create staged borrower portal user object without invite
  const inviteBorrowerToPortalFromLoan = async ({
    dealId,
    borrowerId,
    data = {},
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    // On borrower drilldown
    const endpoint = `${ROUTE_PREFIX}/invite_borrower_to_portal_from_loan/${dealId}/${borrowerId}`;
    /**
     	"""Create a borrower portal user from a deal borrower's contact info and send an invite.
	Stages the borrower, sends the invite, links the portal user back to the deal borrower,
	and adds the dealId to the portal user's dealIds.
	Request body: { sendInvite?: boolean (default true) }"""
     */
    const source = axios.CancelToken.source();
    await axiosPostRequest({
      name: "Invite Borrower to Portal",
      path: endpoint,
      payload: data,
      cancelSource: source,
      onSuccessFn,
      onSuccessMsg: "Borrower invited to portal",
      onCompleteFn,
      onFailFn,
    });
  };

  const getPortalUserForBorrower = async ({
    dealId,
    borrowerId,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    const endpoint = `${ROUTE_PREFIX}/get_portal_user_for_borrower/${dealId}/${borrowerId}`;
    const source = axios.CancelToken.source();
    /**
     """Get the borrower portal user profile linked to a specific borrower on a deal.
	Returns the portal user with files if borrowerPortalUserId is set on the borrower."""
     */
    await axiosGetRequest({
      name: "Get Portal User for Borrower",
      path: endpoint,
      cancelSource: source,
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    });
  };
  // On borrower drilldown
  const syncBorrowerToPortalUser = async ({
    dealId,
    borrowerId,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    const endpoint = `${ROUTE_PREFIX}/sync_borrower_to_portal_user/${dealId}/${borrowerId}`;
    const source = axios.CancelToken.source();

    /**
     	"""Push underwriting data from a deal borrower to the linked borrower portal user.
	Syncs scalar fields (FICO, experience, liquidity), validatedExperience array,
	deal subjectProperties, borrowerEntity → associatedEntities,
	and copies deal document files to the borrower's portal blob container.
	Requires borrowerPortalUserId to be set on the borrower."""
     */
    await axiosPostRequest({
      name: "Sync Borrower to Portal User",
      path: endpoint,
      payload: {},
      cancelSource: source,
      onSuccessFn,
      onSuccessMsg: "Portal user updated with deal borrower data",
      onCompleteFn,
      onFailFn,
    });
  };
  // Delete a borrower from a loan. Server-side this pulls the borrower
  // from `deal.borrowers` and moves it into `deal.deletedBorrowers` so
  // the record is preserved for audit.
  const deleteBorrower = async ({
    loanId,
    borrowerId,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    if (isNil(loanId) || isNil(borrowerId)) {
      console.error("Missing loanId or borrowerId", { loanId, borrowerId });
      return;
    }
    const endpoint = `${ROUTE_PREFIX}/delete_borrower/${loanId}/${borrowerId}`;
    const source = axios.CancelToken.source();
    await axiosPostRequest({
      name: "Delete Borrower",
      path: endpoint,
      payload: {},
      cancelSource: source,
      onSuccessMsg: "Borrower deleted",
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    });
  };

  // Delete a subject property from a loan. Mirrors deleteBorrower —
  // server-side pulls the property and moves it into
  // `deal.deletedSubjectProperties`.
  const deleteSubjectProperty = async ({
    loanId,
    propertyId,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    if (isNil(loanId) || isNil(propertyId)) {
      console.error("Missing loanId or propertyId", { loanId, propertyId });
      return;
    }
    const endpoint = `${ROUTE_PREFIX}/delete_subject_property/${loanId}/${propertyId}`;
    const source = axios.CancelToken.source();
    await axiosPostRequest({
      name: "Delete Subject Property",
      path: endpoint,
      payload: {},
      cancelSource: source,
      onSuccessMsg: "Property deleted",
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    });
  };

  // On borrower drilldown
  const syncPortalUserToBorrower = async ({
    dealId,
    borrowerId,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    const endpoint = `${ROUTE_PREFIX}/sync_portal_user_to_borrower/${dealId}/${borrowerId}`;
    const source = axios.CancelToken.source();

    /**
     	"""Pull portal user profile data onto a deal borrower.
	Syncs contact fields (PORTAL_TO_BORROWER_FIELDS), validatedExperience, and bankDetails.
	Also copies borrower portal document files to the deal's blob container.
	Requires borrowerPortalUserId to be set on the borrower."""
     */
    await axiosPostRequest({
      name: "Sync Portal User to Borrower",
      path: endpoint,
      payload: {},
      cancelSource: source,
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    });
  };
  // ─────────────────────────────────────────────────────────────────────
  // Flood + FundingShield orders — REQUESTS COMMENTED OUT (2026-10-15)
  // ─────────────────────────────────────────────────────────────────────
  // These seven endpoints were served by a separate workflows app that is
  // being phased out. They are NOT on the LOS backend (BASE_URL) yet, so each
  // request below is commented out until the backend adds the route. When it
  // does, uncomment the request — it already targets the default domain — and
  // delete the `endpointNotOnBackendYet` call above it.
  //
  // Until then every call fails fast with a readable error and still settles
  // the caller's callbacks, so no sidebar is left spinning.
  const endpointNotOnBackendYet = (name, { onFailFn, onCompleteFn }) => {
    const error = new Error(
      `${name} isn't available yet — this endpoint is being added to the LOS backend.`,
    );
    console.warn(error.message);
    onFailFn?.(error);
    onCompleteFn?.();
  };

  // Flood certificate ordering — Xactus integration.
  // Sidebar opens the form with prefill from getFloodOrderFormSchema, then
  // POSTs to requestFloodCertificate. queryFloodOrderStatus is for follow-ups
  // when the initial response comes back as `pending` or
  // `order_requires_attention` (avoids billing a duplicate Xactus order).
  const getFloodOrderFormSchema = async ({
    loanId,
    subjectPropertyId,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    if (isNil(loanId) || isNil(subjectPropertyId)) {
      console.error("loanId or subjectPropertyId is missing", {
        loanId,
        subjectPropertyId,
      });
      onFailFn?.();
      onCompleteFn?.();
      return;
    }
    // Commented out until GET /flood/order_form_schema is added to the LOS backend.
    endpointNotOnBackendYet("The flood order form", { onFailFn, onCompleteFn });
    // const params = new URLSearchParams({
    //   dealId: loanId,
    //   subjectPropertyId,
    // });
    // const path = `flood/order_form_schema?${params.toString()}`;
    // const source = axios.CancelToken.source();
    // await axiosGetRequest({
    //   name: "Get Flood Order Form Schema",
    //   path,
    //   cancelSource: source,
    //   onSuccessFn,
    //   onCompleteFn,
    //   onFailFn,
    // });
  };

  // POST /flood/pull/{dealId}/{subjectPropertyId}
  // Strict body contract (post-gate-tightening):
  //   - Body MUST be { inputs: { ... } } — never empty, never an alternate
  //     wrapper key. Empty / unrecognized shapes return 400 Missing Order Inputs.
  //   - inputs.confirmAddress MUST be true or backend returns 400 Address
  //     Confirmation Required (the form-level checkbox enforces this client-side).
  //   - inputs.subjectPropertyId is NOT sent — the URL path is authoritative
  //     and the backend overwrites whatever you pass.
  const requestFloodCertificate = async ({
    loanId,
    subjectPropertyId,
    inputs,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    if (isNil(loanId) || isNil(subjectPropertyId)) {
      console.error("loanId or subjectPropertyId is missing", {
        loanId,
        subjectPropertyId,
      });
      onFailFn?.();
      onCompleteFn?.();
      return;
    }
    if (isNil(inputs) || isEmpty(inputs)) {
      console.error(
        "requestFloodCertificate: refusing to POST without inputs — " +
          "backend would reject with 400 Missing Order Inputs.",
      );
      onFailFn?.();
      onCompleteFn?.();
      return;
    }
    if (inputs.confirmAddress !== true) {
      console.error(
        "requestFloodCertificate: refusing to POST without " +
          "inputs.confirmAddress === true — backend would reject with 400 " +
          "Address Confirmation Required.",
      );
      onFailFn?.();
      onCompleteFn?.();
      return;
    }
    // Commented out until POST /flood/pull/{dealId}/{subjectPropertyId} is
    // added to the LOS backend.
    endpointNotOnBackendYet("Flood certificate ordering", {
      onFailFn,
      onCompleteFn,
    });
    // const path = `flood/pull/${loanId}/${subjectPropertyId}`;
    // const source = axios.CancelToken.source();
    // await axiosPostRequest({
    //   name: "Place Flood Certificate Order",
    //   path,
    //   payload: { inputs },
    //   cancelSource: source,
    //   // No onSuccessMsg — outcome is rendered inside the sidebar.
    //   hideFailMsg: true,
    //   onSuccessFn,
    //   onCompleteFn,
    //   onFailFn,
    // });
  };

  const queryFloodOrderStatus = async ({
    loanId,
    floodCertificationId,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    if (isNil(loanId) || isNil(floodCertificationId)) {
      console.error("loanId or floodCertificationId is missing", {
        loanId,
        floodCertificationId,
      });
      onFailFn?.();
      onCompleteFn?.();
      return;
    }
    // Commented out until POST /flood/status_query/{dealId}/{floodCertificationId}
    // is added to the LOS backend.
    endpointNotOnBackendYet("Flood order status checks", {
      onFailFn,
      onCompleteFn,
    });
    // const path = `flood/status_query/${loanId}/${floodCertificationId}`;
    // const source = axios.CancelToken.source();
    // await axiosPostRequest({
    //   name: "Query Flood Order Status",
    //   path,
    //   payload: {},
    //   cancelSource: source,
    //   hideFailMsg: true,
    //   onSuccessFn,
    //   onCompleteFn,
    //   onFailFn,
    // });
  };

  // FundingShield Guardian — wire/closing-fraud verification.
  //   GET  /funding_shield/order_form_schema?dealId=...
  //   POST /funding_shield/order/{dealId}                   { inputs: {...} }
  //   POST /funding_shield/status/{dealId}/{txId}/check     no body
  //   POST /funding_shield/certificate/{dealId}/{txId}/download   no body
  const getFundingShieldOrderFormSchema = async ({
    loanId,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    if (isNil(loanId)) {
      console.error("loanId missing", { loanId });
      onFailFn?.();
      onCompleteFn?.();
      return;
    }
    // Commented out until GET /funding_shield/order_form_schema is added to
    // the LOS backend.
    endpointNotOnBackendYet("The FundingShield order form", {
      onFailFn,
      onCompleteFn,
    });
    // const params = new URLSearchParams({ dealId: loanId });
    // const path = `funding_shield/order_form_schema?${params.toString()}`;
    // const source = axios.CancelToken.source();
    // await axiosGetRequest({
    //   name: "Get FundingShield Order Form Schema",
    //   path,
    //   cancelSource: source,
    //   onSuccessFn,
    //   onCompleteFn,
    //   onFailFn,
    // });
  };

  const placeFundingShieldOrder = async ({
    loanId,
    inputs,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    if (isNil(loanId)) {
      console.error("loanId missing", { loanId });
      onFailFn?.();
      onCompleteFn?.();
      return;
    }
    if (isNil(inputs) || isEmpty(inputs)) {
      console.error(
        "placeFundingShieldOrder: refusing to POST without inputs — backend would reject with 400 Missing Order Inputs.",
      );
      onFailFn?.();
      onCompleteFn?.();
      return;
    }
    if (inputs.confirmAddress !== true) {
      console.error(
        "placeFundingShieldOrder: refusing to POST without confirmAddress=true — backend would reject with 400 Address Confirmation Required.",
      );
      onFailFn?.();
      onCompleteFn?.();
      return;
    }
    // Commented out until POST /funding_shield/order/{dealId} is added to the
    // LOS backend.
    endpointNotOnBackendYet("FundingShield ordering", {
      onFailFn,
      onCompleteFn,
    });
    // const path = `funding_shield/order/${loanId}`;
    // const source = axios.CancelToken.source();
    // await axiosPostRequest({
    //   name: "Place FundingShield Order",
    //   path,
    //   payload: { inputs },
    //   cancelSource: source,
    //   hideFailMsg: true,
    //   onSuccessFn,
    //   onCompleteFn,
    //   onFailFn,
    // });
  };

  const checkFundingShieldStatus = async ({
    loanId,
    transactionId,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    if (isNil(loanId) || isNil(transactionId)) {
      console.error("loanId or transactionId missing", {
        loanId,
        transactionId,
      });
      onFailFn?.();
      onCompleteFn?.();
      return;
    }
    // Commented out until POST /funding_shield/status/{dealId}/{transactionId}/check
    // is added to the LOS backend.
    endpointNotOnBackendYet("FundingShield status checks", {
      onFailFn,
      onCompleteFn,
    });
    // const path = `funding_shield/status/${loanId}/${transactionId}/check`;
    // const source = axios.CancelToken.source();
    // await axiosPostRequest({
    //   name: "Check FundingShield Status",
    //   path,
    //   payload: {},
    //   cancelSource: source,
    //   hideFailMsg: true,
    //   onSuccessFn,
    //   onCompleteFn,
    //   onFailFn,
    // });
  };

  const downloadFundingShieldCertificate = async ({
    loanId,
    transactionId,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    if (isNil(loanId) || isNil(transactionId)) {
      console.error("loanId or transactionId missing", {
        loanId,
        transactionId,
      });
      onFailFn?.();
      onCompleteFn?.();
      return;
    }
    // Commented out until POST /funding_shield/certificate/{dealId}/{transactionId}/download
    // is added to the LOS backend.
    endpointNotOnBackendYet("FundingShield certificate download", {
      onFailFn,
      onCompleteFn,
    });
    // const path = `funding_shield/certificate/${loanId}/${transactionId}/download`;
    // const source = axios.CancelToken.source();
    // await axiosPostRequest({
    //   name: "Download FundingShield Certificate",
    //   path,
    //   payload: {},
    //   cancelSource: source,
    //   hideFailMsg: true,
    //   onSuccessFn,
    //   onCompleteFn,
    //   onFailFn,
    // });
  };

  // ─────────────────────────────────────────────────────────────────────
  // Internal Appraisal Review orders (loan-processing)
  // ─────────────────────────────────────────────────────────────────────
  // Temporary intake home for the "Order Internal Appraisal Review" workflow.
  // These live on the first-party LOS backend under /loan-processing (default
  // domain + standard bearer auth), NOT the old workflows host the flood /
  // funding-shield endpoints used. Contract: frontend_documentation/
  // internal-appraisal-orders.md. Treat `_id` as the order key.

  // GET /loan-processing/{dealId}/appraisal-orders → array of orders, newest first.
  const getAppraisalOrders = async ({
    dealId,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    if (isNil(dealId)) {
      console.error("getAppraisalOrders: dealId missing", { dealId });
      onFailFn?.();
      onCompleteFn?.();
      return;
    }
    const source = axios.CancelToken.source();
    await axiosGetRequest({
      name: "Get Appraisal Review Orders",
      path: `loan-processing/${dealId}/appraisal-orders`,
      cancelSource: source,
      hideFailMsg: true,
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    });
  };

  // POST /loan-processing/{dealId}/appraisal-orders → created order (201).
  // Body: { appraisalDocOids: [..], subjectPropertyId, requestedByDate, instructions? }
  // `appraisalDocOids` (list, backend 2026-09-11) carries every appraisal on
  // the order — all are attached to the Internal Appraisers email. We also send
  // the singular `appraisalDocOid` (= first id) so a backend that predates the
  // list field still accepts the request. A singular `appraisalDocOid` argument
  // is still accepted here for older callers.
  const createAppraisalOrder = async ({
    dealId,
    appraisalDocOids,
    appraisalDocOid,
    subjectPropertyId,
    requestedByDate,
    instructions,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    const rawOids = isArray(appraisalDocOids)
      ? appraisalDocOids
      : [isNil(appraisalDocOids) ? appraisalDocOid : appraisalDocOids];
    // Dedupe, keep first-appearance order (mirrors the backend).
    const oids = [
      ...new Set(rawOids.filter((o) => !isNil(o) && o !== "").map(String)),
    ];
    if (
      isNil(dealId) ||
      isEmpty(oids) ||
      isNil(subjectPropertyId) ||
      isNil(requestedByDate)
    ) {
      console.error("createAppraisalOrder: missing required fields", {
        dealId,
        appraisalDocOids: oids,
        subjectPropertyId,
        requestedByDate,
      });
      onFailFn?.();
      onCompleteFn?.();
      return;
    }
    const payload = {
      appraisalDocOids: oids,
      appraisalDocOid: oids[0],
      subjectPropertyId,
      requestedByDate,
    };
    const trimmedInstructions =
      typeof instructions === "string" ? instructions.trim() : instructions;
    if (!isEmpty(trimmedInstructions)) {
      payload.instructions = trimmedInstructions;
    }
    const source = axios.CancelToken.source();
    await axiosPostRequest({
      name: "Order Internal Appraisal Review",
      path: `loan-processing/${dealId}/appraisal-orders`,
      payload,
      cancelSource: source,
      hideFailMsg: true,
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    });
  };

  // GET /loan-processing/appraisal-orders/queue?status=&assignedTo=
  // The internal-appraisal team's worklist. Both filters optional; backend
  // sorts by requestedByDate ascending (soonest due first). We fetch the full
  // queue and filter lanes / "my queue" client-side.
  const getAppraisalOrderQueue = async ({
    status,
    assignedTo,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    const params = new URLSearchParams();
    if (!isNil(status) && status !== "") params.set("status", status);
    if (!isNil(assignedTo) && assignedTo !== "")
      params.set("assignedTo", assignedTo);
    const qs = params.toString();
    const source = axios.CancelToken.source();
    await axiosGetRequest({
      name: "Get Appraisal Review Queue",
      path: `loan-processing/appraisal-orders/queue${qs ? `?${qs}` : ""}`,
      cancelSource: source,
      hideFailMsg: true,
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    });
  };

  // Queue transition actions. Each returns the updated order; a 409 means
  // someone else already claimed/transitioned it (refetch, don't retry).
  const transitionAppraisalOrder = async ({
    orderId,
    action, // "assign" | "in-review" | "complete" | "cancel"
    name,
    payload = {},
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    if (isNil(orderId)) {
      console.error("transitionAppraisalOrder: orderId missing", { orderId });
      onFailFn?.();
      onCompleteFn?.();
      return;
    }
    const source = axios.CancelToken.source();
    await axiosPostRequest({
      name,
      path: `loan-processing/appraisal-orders/${orderId}/${action}`,
      payload,
      cancelSource: source,
      hideFailMsg: true,
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    });
  };

  const assignAppraisalOrder = (args) =>
    transitionAppraisalOrder({
      ...args,
      action: "assign",
      name: "Assign Appraisal Review",
    });

  const markAppraisalOrderInReview = (args) =>
    transitionAppraisalOrder({
      ...args,
      action: "in-review",
      name: "Mark Appraisal Review In Review",
    });

  const completeAppraisalOrder = ({
    orderId,
    reviewOutcome,
    reviewNotes,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    if (isNil(reviewOutcome)) {
      console.error("completeAppraisalOrder: reviewOutcome is required");
      onFailFn?.();
      onCompleteFn?.();
      return;
    }
    return transitionAppraisalOrder({
      orderId,
      action: "complete",
      name: "Complete Appraisal Review",
      payload: { reviewOutcome, reviewNotes: reviewNotes || "" },
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    });
  };

  const cancelAppraisalOrder = ({
    orderId,
    cancelReason,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) =>
    transitionAppraisalOrder({
      orderId,
      action: "cancel",
      name: "Cancel Appraisal Review",
      payload: cancelReason ? { cancelReason } : {},
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    });

  return {
    error,
    loading,
    axiosGetIsLoading,
    axiosPostIsLoading,
    getAppraisalOrders,
    createAppraisalOrder,
    getAppraisalOrderQueue,
    assignAppraisalOrder,
    markAppraisalOrderInReview,
    completeAppraisalOrder,
    cancelAppraisalOrder,
    getFloodOrderFormSchema,
    requestFloodCertificate,
    queryFloodOrderStatus,
    getFundingShieldOrderFormSchema,
    placeFundingShieldOrder,
    checkFundingShieldStatus,
    downloadFundingShieldCertificate,
    getUnderwritingPipeline,
    getLoanById,
    getLoanEvents,
    updateLoanData,
    updateMilestoneStatus,
    createDealComment,
    createEnhancedDealComment,
    updateDealComment,
    addLoanCounterparty,
    findContactByEmail,
    createUnnestedObject,
    createSubjectProperty,
    updateSubjectProperty,
    createBorrower,
    updateBorrower,
    assignSubmissionToDeal,
    updateLoanPermittedUsers,
    populateLoanFormFromExcel,
    populateLoanFormFromLoanApplication,
    createLoanFromExcel,
    createLoanWithFileUploads,
    createLoan,
    unsetLoanDataFields,
    updateUnderwritingNotes,
    addPermittedUserToLoan,
    removeAssociation,
    updateUnnestedObject,
    createLoanException,
    createObjectInList,
    updateObjectInList,
    createLoanFromApplication,
    removeLoanTag,
    updateLoanRelatedContact,
    deleteLoanRelatedContact,
    addLoanTag,
    addLoanRelatedContact,
    createSubjectPropertyUnit,
    updateSubjectPropertyUnit,
    addAssociation,
    markDealClosed,
    markDealDead,
    setBorrowerClassificationOverride,
    getZillowZipcodeData,
    createLoanFromSubmission,
    updateLoanStatusOrSubStatus,
    updateSubStatus,
    searchBorrowerPortalUsers,
    searchSalesforceContacts,
    searchLoanContacts,
    searchSalesforceEntities,
    createSalesforceOpportunity,
    linkBorrowerToPortalUser,
    inviteBorrowerToPortalFromLoan,
    getPortalUserForBorrower,
    syncBorrowerToPortalUser,
    syncPortalUserToBorrower,
    deleteBorrower,
    deleteSubjectProperty,
  };
};
